import {
  Form,
  Modal,
  message,
  Switch,
  Row,
  Col,
} from "antd";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../hooks/api";
import { pathAPI } from "../../utils/pathApi";
import { Box } from "@mui/material";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import { FormInstance } from "antd/lib";
import { Typography } from "@mui/material";

interface SystemConfig {
  id: number;
  config_key: string;
  config_value: string;
  description: string;
  status: number;
  created_date: string;
  last_modified_date: string;
}

interface ModalProps {
  open: boolean;
  onCancel: () => void;
  reloadData: () => void;
}

interface EditSystemConfigProps extends ModalProps {
  configData: SystemConfig;
}

// Edit Component
export const EditSystemConfigModal: React.FC<EditSystemConfigProps> = ({
  open,
  reloadData,
  onCancel,
  configData
}) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const [loading, setLoading] = useState(false);

  // Set form values when modal opens
  useEffect(() => {
    if (open && configData) {
      formRef.current?.setFieldsValue({
        config_key: configData.config_key,
        config_value: configData.config_value,
        description: configData.description,
        status: configData.status === 1
      });
    }
  }, [configData, open]);

  const onFinish = (values: any) => {
    setLoading(true);
    api.PUT(`${pathAPI.systemConfig}/${configData.id}`, { 
      ...values, 
      status: values.status ? 1 : 0 
    }, {}, true)
      .then(() => {
        message.success(t("system_config.form.updated_ok"));
        onCancel();
        reloadData();
      })
      .catch((error: any) => {
        // Check if error contains data (successful response but treated as error)
        if (error?.data?.data) {
          message.success(t("system_config.form.updated_ok"));
          onCancel();
          reloadData();
        } else {
          message.error(t("system_config.form.update_error"));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onCancel}
      width={600}
      destroyOnClose
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("system_config.form.edit_title")}
      </Typography>
      <Form 
        onFinish={onFinish}
        ref={formRef}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        preserve={false}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              name="config_key"
              label={t("system_config.form.key")}
              rules={[{ required: true, message: t("system_config.form.key_required") }]}
            >
              <InputField />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="config_value"
              label={t("system_config.form.value")}
              rules={[{ required: true, message: t("system_config.form.value_required") }]}
            >
              <InputField />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t("system_config.form.description")}
              rules={[{ required: true, message: t("system_config.form.description_required") }]}
            >
              <InputField multiline minRows={3} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="status"
              label={t("system_config.form.status")}
              valuePropName="checked"
            >
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
          </Col>
        </Row>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <CancelButton onClick={onCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton type="submit" loading={loading}>
            {t("system_config.form.update")}
          </ActionButton>
        </Box>
      </Form>
    </Modal>
  );
};

// Create Component
export const CreateSystemConfigModal: React.FC<ModalProps> = ({
  open,
  reloadData,
  onCancel,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const [loading, setLoading] = useState(false);
  

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      formRef.current?.resetFields();
      formRef.current?.setFieldsValue({ status: true });
    }
  }, [open]);

  const onFinish = (values: any) => {
    setLoading(true);
    api.POST(pathAPI.systemConfig, { 
      ...values, 
      status: values.status ? 1 : 0 
    }, {}, true)
      .then(() => {
        message.success(t("system_config.form.added_ok"));
        onCancel();
        reloadData();
      })
      .catch((error: any) => {
        // Check if error contains data (successful response but treated as error)
        if (error?.data?.data) {
          message.success(t("system_config.form.added_ok"));
          onCancel();
          reloadData();
        } else {
          message.error(t("system_config.form.add_error"));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onCancel}
      width={600}
      destroyOnClose
      style={{ top: 20 }}
      centered
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("system_config.form.add_title")}
      </Typography>
      <Form 
        onFinish={onFinish}
        ref={formRef}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        initialValues={{ status: true }}
        preserve={false}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              name="config_key"
              label={t("system_config.form.key")}
              rules={[{ required: true, message: t("system_config.form.key_required") }]}
            >
              <InputField />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="config_value"
              label={t("system_config.form.value")}
              rules={[{ required: true, message: t("system_config.form.value_required") }]}
            >
              <InputField />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t("system_config.form.description")}
              rules={[{ required: true, message: t("system_config.form.description_required") }]}
            >
              <InputField multiline minRows={3} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="status"
              label={t("system_config.form.status")}
              valuePropName="checked"
            >
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
          </Col>
        </Row>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <CancelButton onClick={onCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton type="submit" loading={loading}>
            {t("system_config.form.add")}
          </ActionButton>
        </Box>
      </Form>
    </Modal>
  );
};

// Legacy component for compatibility
const AddSystemConfig: React.FC<any> = (props) => {
  console.log("Legacy component called with props:", props);
  // For backward compatibility
  if (props.configData) {
    return <EditSystemConfigModal {...props} configData={props.configData} />;
  }
  return <CreateSystemConfigModal {...props} />;
};

export default AddSystemConfig;
